import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { buildDocument, h1, table } from "@/lib/documentGenerator/docTheme";
import { APD_FIELDS, PARALLEL_SECTIONS } from "@/lib/basProcess/constants";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export const generateStagePlanWordDocument = async (
  name: string,
  content: { apd?: Record<string, string>; stages?: any[] },
  projectName?: string
) => {
  const stages = content.stages || [];
  const apd = content.apd || {};
  const documentName = name || "Skedesplan / APD-plan";

  const stamp = await createStampForCurrentUser({
    docNumber: "26",
    documentName,
    projectName,
  });

  const children: (Paragraph | Table)[] = [
    h1("1. Skeden"),
    table(
      ["Skede", "Period", "Arbetsmoment", "Samtidiga entreprenörer", "Riskpunkter", "Ansvarig"],
      stages.map((s: any) => [
        s.name,
        [s.start, s.end].filter(Boolean).join(" – "),
        s.activities,
        s.contractors,
        s.risks,
        s.responsible,
      ]),
      { columnWidths: [1500, 1500, 1900, 1700, 1600, 1160] }
    ),

    h1("2. APD-förutsättningar"),
    table(
      ["Område", "Beskrivning"],
      APD_FIELDS.map((f) => [f.label, apd[f.key]]),
      { columnWidths: [3000, 6360] }
    ),
  ];
  const footer = await documentFooter({ docNumber: "26", documentType: "skedesplan" });

  const doc = buildDocument({
    meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      docNumber: "26", documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `32_Skedesplan_${(name || "APD").replace(/\s+/g, "_")}.docx`, {
    documentType: "skedesplan",
    docNumber: "26",
    documentName,
    snapshot: content,
    stamp,
  });
};

export const generateParallelCoordinationWordDocument = async (
  name: string,
  content: Record<string, any[]>,
  projectName?: string
) => {
  const documentName = name || "Parallell projektering och produktion";
  const stamp = await createStampForCurrentUser({ documentName, projectName });

  const children: (Paragraph | Table)[] = PARALLEL_SECTIONS.flatMap((section, i) => [
    h1(`${i + 1}. ${section.title}`),
    table(
      ["Punkt", "Beskrivning", "Datum", "Ansvarig roll", "Kommentar"],
      (content[section.key] || []).map((r: any) => [
        r.title,
        r.detail,
        r.date,
        r.owner,
        r.consequence,
      ]),
      { columnWidths: [1900, 2700, 1300, 1600, 1860] }
    ),
  ]);
  const footer = await documentFooter({ docNumber: undefined, documentType: "parallell_samordning" });

  const doc = buildDocument({
    meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Samordning_${(name || "Parallell").replace(/\s+/g, "_")}.docx`, {
    documentType: "parallell_samordning",
    documentName,
    snapshot: content,
    stamp,
  });
};
